import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { MediGenieLogo } from "./MediGenieLogo";
import { AssistantWidget } from "./AssistantWidget";
import { ImpersonationBanner } from "./ImpersonationBanner";
import { LogOut, User } from "lucide-react";
import { usePermissions } from "../lib/permissions";
import api from "../lib/api";
import { mgGuideHref } from "../lib/userGuide";

export function DashboardLayout({ links, title, children, guideRole }) {
  const { user, logout, selectBranch } = useAuth();
  const { perms } = usePermissions();
  const [branchContext, setBranchContext] = useState(null);
  const isImp = Boolean(user?.impersonated_by);
  const guideHref = mgGuideHref(user, branchContext, guideRole);
  const visibleLinks = links.filter((link) => !link.perm || perms.has("*") || perms.has(link.perm));
  useEffect(() => {
    if (!user?.clinic_id || user?.role === "super_admin" || user?.workspace === "pharmacy") return;
    api.get("/organization/context").then((r) => setBranchContext(r.data)).catch(() => setBranchContext(null));
  }, [user?.clinic_id, user?.role, user?.workspace]);
  const changeBranch = async (event) => {
    const branchId = event.target.value;
    if (!branchId || branchId === user?.clinic_id) return;
    await selectBranch(branchId);
    window.location.reload();
  };
  return (
    <div className="min-h-screen bg-[#F8FAFC] overflow-x-hidden">
      {isImp && <ImpersonationBanner />}
      <aside className={`fixed left-0 ${isImp ? "top-10" : "top-0"} h-screen w-64 bg-mg-navy text-white hidden lg:flex flex-col z-40`}>
        <div className="px-5 py-5 border-b border-slate-700/50">
          <MediGenieLogo dark />
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {visibleLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) => `mg-sidebar-link ${isActive ? "active" : ""}`}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.icon}
              <span>{l.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-700/50">
          <NavLink to="/my-profile" className="flex items-center gap-3 mb-3 rounded-lg hover:bg-white/10 p-1" data-testid="my-profile-link">
            <div className="w-9 h-9 mg-gradient-magical rounded-full flex items-center justify-center text-mg-navy font-bold">
              {(user?.name || "?").charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-[11px] text-slate-400 capitalize">{user?.role?.replace("_", " ")}</div>
            </div>
          </NavLink>
          <a href={guideHref} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 mb-3 rounded-lg px-2 py-2 text-sm font-semibold text-slate-200 hover:bg-white/10 hover:text-white" data-testid="dashboard-user-guide">User Guide</a>
          <button
            onClick={logout}
            className="w-full text-xs text-slate-300 hover:text-white flex items-center gap-2 py-2"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>
      <main className={`ml-0 lg:ml-64 min-w-0 ${isImp ? "pt-10" : ""}`}>
        <header className="bg-white border-b border-slate-200 px-4 sm:px-8 py-4 sm:py-5 flex items-center justify-between gap-3 sticky top-0 z-30 min-w-0">
          <h1 className="text-lg sm:text-xl font-bold text-mg-navy truncate" data-testid="page-title">{title}</h1>
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <NavLink to="/pharmacy-panel" className="text-sm text-mg-medical">Pharmacy workspace</NavLink>
            {branchContext?.branches?.length > 1 && (
              <label className="text-xs text-slate-500">
                <span className="sr-only">Current branch</span>
                <select className="mg-input py-2" value={user?.clinic_id || ""} onChange={changeBranch} data-testid="branch-selector">
                  {branchContext.branches.map((branch) => (
                    <option key={branch.clinic_id} value={branch.clinic_id}>{branch.name} · {branch.timezone}</option>
                  ))}
                </select>
              </label>
            )}
            <div className="text-sm text-slate-600 hidden md:block">{user?.email}</div>
            <div className="w-9 h-9 bg-slate-100 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-slate-500" />
            </div>
          </div>
        </header>
        <nav className="lg:hidden bg-mg-navy px-3 py-2 flex gap-1 overflow-x-auto" aria-label="Workspace navigation">
          <a href={guideHref} target="_blank" rel="noopener noreferrer" className="shrink-0 rounded-lg px-3 py-2 text-xs font-semibold text-slate-200" data-testid="dashboard-user-guide-mobile">User Guide</a>
          {visibleLinks.map((link) => <NavLink key={link.to} to={link.to} end={link.end}
            className={({isActive}) => `shrink-0 rounded-lg px-3 py-2 text-xs font-semibold ${isActive ? "bg-white text-mg-navy" : "text-slate-200"}`}
            data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}>
            {link.label}
          </NavLink>)}
        </nav>
        <div className="p-4 sm:p-8 min-w-0">{children}</div>
      </main>
      {user?.workspace !== "pharmacy" && <AssistantWidget />}
    </div>
  );
}
